import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '../Card';
import { formatChange } from '@/shared/utils/format';
import styles from './MetricCard.module.css';

export type MetricTone = 'default' | 'red' | 'yellow' | 'green' | 'blue';

interface MetricCardProps {
  title: string;
  value: string | number;
  /** Rendered after the value in muted color, outside the tone tint (e.g. units, symbols). */
  valueSuffix?: string;
  change?: number;
  changeLabel?: string;
  sub?: string;
  icon?: LucideIcon;
  lastUpdated?: string;
  /** Tints the value. Used to draw attention to urgent / positive / neutral metrics. */
  tone?: MetricTone;
}

export function MetricCard({
  title,
  value,
  valueSuffix,
  change,
  changeLabel,
  sub,
  icon: Icon,
  lastUpdated,
  tone = 'default',
}: MetricCardProps) {
  const isPositive = change !== undefined && change > 0;
  const isNegative = change !== undefined && change < 0;

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {Icon && (
          <span className={styles.icon}>
            <Icon size={16} strokeWidth={1.75} aria-hidden />
          </span>
        )}
      </div>
      <div className={styles.valueRow}>
        <span className={`${styles.value} ${styles[`tone_${tone}`]}`}>{value}</span>
        {valueSuffix && <span className={styles.suffix}>{valueSuffix}</span>}
      </div>
      {sub && <div className={styles.sub}>{sub}</div>}
      {change !== undefined && (
        <div
          className={`${styles.change} ${isPositive ? styles.positive : ''} ${isNegative ? styles.negative : ''}`}
        >
          <span>{formatChange(change)}</span>
          {changeLabel && <span className={styles.changeLabel}> {changeLabel}</span>}
        </div>
      )}
      {lastUpdated && <span className={styles.lastUpdated}>Updated {lastUpdated}</span>}
    </Card>
  );
}
